// API-client voor de DocR webapp.
// Paden zijn relatief; in dev routet Vite via proxy naar de Worker,
// in productie staat de webapp op app.docr.nl en de API op hetzelfde domein
// (via Pages routing of via een custom worker-subdomein). Voor nu werken
// we met absolute URL via env var.

// Default empty -> relative URL (same-origin). In productie loopt /api/* via een
// Worker Route op app.docr.nl, dus geen CORS en één Cloudflare Access cookie.
// In dev via Vite proxy (zie vite.config.ts) ook richting de prod-API.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
	constructor(public status: number, public body: unknown) {
		super(`API error ${status}`);
	}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(`${API_BASE}${path}`, {
		credentials: 'include',
		...init,
		headers: {
			'accept': 'application/json',
			...(init?.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
			...init?.headers,
		},
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({ error: res.statusText }));
		throw new ApiError(res.status, body);
	}
	return res.json() as Promise<T>;
}

// --- Types (mirror van API-responses) ---

export interface Me {
	id: string;
	email: string;
	name: string;
	role: 'admin' | 'user';
	entity_codes: string[] | 'all';
	can_view_invoices: boolean;
	can_approve_exact: boolean;
}

export interface Entity {
	id: string;
	code: string;
	name: string;
	r2_bucket_name: string;
	has_inkoop?: boolean;
	has_verkoop?: boolean;
}

export interface EntityFull extends Entity {
	intake_email: string;
	active: number | boolean;
	created_at?: string;
	updated_at?: string;
}

export type DocumentStatus =
	| 'received'
	| 'processing'
	| 'classified'
	| 'completed'
	| 'error'
	| 'duplicate'
	| 'skipped';

export type DocumentType =
	| 'invoice_incoming'
	| 'invoice_outgoing'
	| 'credit_note'
	| 'reminder'
	| 'contract'
	| 'employment_contract'
	| 'quote'
	| 'letter_incoming'
	| 'letter_outgoing'
	| 'government_decision'
	| 'insurance_claim'
	| 'policy'
	| 'payslip'
	| 'annual_statement'
	| 'report'
	| 'receipt'
	| 'other';

export interface DocumentListItem {
	id: string;
	entity_id: string;
	entity_code: string;
	entity_name: string;
	status: DocumentStatus;
	source: string;
	original_filename: string | null;
	document_type: DocumentType | null;
	direction: 'incoming' | 'outgoing' | null;
	counterparty_name: string | null;
	addressee: string | null;
	document_date: string | null;
	amount_total: string | null;
	currency: string | null;
	summary: string | null;
	tags: string | string[] | null;
	confidence_overall: string | null;
	needs_review: number | boolean;
	created_at: string;
	processed_at: string | null;
}

export interface DocumentDetail extends DocumentListItem {
	counterparty_vat: string | null;
	counterparty_iban: string | null;
	counterparty_address: string | null;
	counterparty_phone: string | null;
	counterparty_email: string | null;
	counterparty_website: string | null;
	counterparty_kvk: string | null;
	due_date: string | null;
	reference: string | null;
	reference_type: string | null;
	amount_excl_vat: string | null;
	vat_amount: string | null;
	insurance_tax_amount: string | null;
	subject: string | null;
	case_reference: string | null;
	notes: string | null;
	status_reason: string | null;
	extracted_text: string | null;
	extracted_text_truncated: number | boolean;
	confidence_per_field: Record<string, number | null> | string | null;
	content_hash_sha256: string;
	file_size_bytes: number;
	mime_type: string;
	r2_key: string;
	vector_indexed_at: string | null;
	source_email_from: string | null;
	source_email_subject: string | null;
	target: string | null;
	exact_entry_id: string | null;
	exact_status: ExactStatus | null;
	payment_status: ExactPaymentStatus | null;
	payment_status_synced_at: string | null;
	payment_date: string | null;
}

export interface AuditEvent {
	id: string;
	entity_id: string;
	document_id: string | null;
	event_type: string;
	event_data: Record<string, unknown> | string | null;
	error_message: string | null;
	occurred_at: string;
}

export type ExactStatus =
	| 'pending_review'
	| 'approved'
	| 'sent'
	| 'failed'
	| 'not_applicable'
	| 'already_present';

export type ExactPaymentStatus = 'paid' | 'partial' | 'open';

export interface InkoopListItem {
	id: string;
	entity_id: string;
	entity_code: string;
	entity_name: string;
	original_filename: string | null;
	counterparty_name: string | null;
	document_date: string | null;
	amount_total: string | null;
	currency: string | null;
	exact_status: ExactStatus | null;
	exact_error_reason: string | null;
	payment_status: ExactPaymentStatus | null;
	payment_status_synced_at: string | null;
	created_at: string;
	processed_at: string | null;
}

export interface InkoopProposalLine {
	description: string;
	ledger_account: string | null;
	vat_code: string | null;
	vat_percent: string | null;
	amount_excl_vat: string | null;
	vat_amount: string | null;
	is_shipping: boolean;
}

export interface InkoopProposalHeader {
	exact_account_id: string | null;
	factuurnummer: string | null;
	order_reference: string | null;
	factuurdatum: string | null;
	vervaldatum: string | null;
	is_credit_note: boolean;
	line_split_mode: 'single' | 'multi';
	comment: string;
	payment_reference: string | null;
}

export interface InkoopProposal {
	header: InkoopProposalHeader;
	lines: InkoopProposalLine[];
}

export interface VerkoopListItem {
	id: string;
	entity_id: string;
	entity_code: string;
	entity_name: string;
	original_filename: string | null;
	counterparty_name: string | null;
	document_date: string | null;
	amount_total: string | null;
	currency: string | null;
	exact_status: ExactStatus | null;
	exact_error_reason: string | null;
	payment_status: ExactPaymentStatus | null;
	payment_status_synced_at: string | null;
	created_at: string;
	processed_at: string | null;
}

export interface VerkoopProposalLine {
	description: string;
	ledger_account: string | null;
	vat_code: string | null;
	vat_percent: string | null;
	amount_excl_vat: string | null;
	vat_amount: string | null;
}

export interface VerkoopProposalHeader {
	customer_exact_id: string | null;
	customer_name: string | null;
	customer_code: string | null;
	factuurnummer: string | null;
	factuurdatum: string | null;
	vervaldatum: string | null;
	description: string;
	is_credit_note: boolean;
}

export interface VerkoopProposal {
	header: VerkoopProposalHeader;
	lines: VerkoopProposalLine[];
}

export interface ExactCreditor {
	exact_id: string;
	code: string | null;
	name: string;
	vat_number: string | null;
	chamber_of_commerce: string | null;
	our_account: string | null;
	iban: string | null;
	address_line1: string | null;
	postcode: string | null;
	city: string | null;
	country: string | null;
}

export interface ExactCustomer {
	exact_id: string;
	code: string | null;
	name: string;
	vat_number: string | null;
	chamber_of_commerce: string | null;
	iban: string | null;
	address_line1: string | null;
	postcode: string | null;
	city: string | null;
	country: string | null;
	email: string | null;
}

export interface ExactLedger {
	exact_id: string;
	code: string;
	description: string;
	type: string | null;
	type_description: string | null;
}

export interface ExactJournal {
	exact_id: string;
	code: string;
	description: string;
	type: string | null;
	type_description: string | null;
}

export interface ExactEntityDefaults {
	default_journal: string | null;
	default_purchase_ledger: string | null;
	default_shipping_ledger: string | null;
	is_vat_exempt: boolean;
}

export interface ExactVatCode {
	exact_id: string;
	code: string;
	description: string;
	percentage: string | null;
	type: string | null;
	is_reverse_charged?: number | boolean;
}

export interface ExactCreditorDefaults {
	default_ledger_account: string | null;
	shipping_ledger_account: string | null;
	line_split_mode: 'single' | 'multi';
	default_btw_code: string | null;
	payment_reference_template: string | null;
}

export interface ExactCredentialsInfo {
	connected: boolean;
	credentials: {
		division_id: number;
		connected_at: string;
		connected_by_user_id: string;
		last_used_at: string | null;
		access_token_expires_at: string;
	} | null;
	sync: {
		last_creditors_sync: string | null;
		last_ledgers_sync: string | null;
		last_vat_codes_sync: string | null;
		last_journals_sync: string | null;
	} | null;
	cache_counts: {
		creditors: number;
		ledgers: number;
		vat_codes: number;
		journals: number;
	} | null;
}

// --- API calls ---

export interface M365App {
	id: string;
	tenant_id: string;
	client_id: string;
	display_name: string;
	client_secret_expires_at: string | null;
	has_secret: number | boolean;
	created_at: string;
	updated_at: string;
}

export interface M365Mailbox {
	id: string;
	m365_app_id: string;
	mailbox_address: string;
	mailbox_user_id: string;
	default_entity_id: string;
	processed_folder_id: string;
	processing_mode: string;
	poll_interval_minutes: number;
	active: number | boolean;
	last_polled_at: string | null;
	last_error: string | null;
	created_at: string;
	updated_at: string;
	app_display_name: string;
	tenant_id: string;
	entity_code: string;
	entity_name: string;
	// Outgoing-detectie config (migration 0014). NULL/'[]' = niet geconfigureerd.
	outgoing_folder_id: string | null;
	outgoing_folder_name: string | null;
	outgoing_sender_patterns: string; // JSON-array als string
	outgoing_subject_patterns: string; // JSON-array als string
	outgoing_match_mode: 'or' | 'and';
}

export const api = {
	me: () => request<Me>('/api/me'),
	entities: () => request<{ count: number; entities: Entity[] }>('/api/entities'),
	documents: (params: Record<string, string | number | boolean | undefined> = {}) => {
		const q = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== '') q.set(k, String(v));
		}
		const qs = q.toString();
		return request<{ count: number; items: DocumentListItem[]; limit: number; offset: number }>(
			`/api/documents${qs ? `?${qs}` : ''}`,
		);
	},
	document: (id: string) =>
		request<{ document: DocumentDetail; audit_trail: AuditEvent[] }>(`/api/documents/${id}`),
	documentTags: () => request<{ tags: string[] }>(`/api/documents/tags`),
	exactCredentials: (entityCode: string) =>
		request<ExactCredentialsInfo>(`/api/exact/admin/credentials/${encodeURIComponent(entityCode)}`),
	exactAuthUrl: (entityCode: string) =>
		request<{ url: string }>(`/api/exact/admin/auth-url?entity_code=${encodeURIComponent(entityCode)}`),
	inkoopfacturen: (params: Record<string, string | number | undefined> = {}) => {
		const q = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== '') q.set(k, String(v));
		}
		const qs = q.toString();
		return request<{ count: number; items: InkoopListItem[]; limit: number; offset: number }>(
			`/api/inkoopfacturen${qs ? `?${qs}` : ''}`,
		);
	},
	inkoopfactuur: (id: string) =>
		request<{ document: Record<string, unknown> & { entity_code: string; entity_name: string }; proposal: InkoopProposal | null }>(
			`/api/inkoopfacturen/${id}`,
		),
	verkoopfacturen: (params: Record<string, string | number | undefined> = {}) => {
		const q = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== '') q.set(k, String(v));
		}
		const qs = q.toString();
		return request<{ count: number; items: VerkoopListItem[]; limit: number; offset: number }>(
			`/api/verkoopfacturen${qs ? `?${qs}` : ''}`,
		);
	},
	verkoopfactuur: (id: string) =>
		request<{ document: Record<string, unknown> & { entity_code: string; entity_name: string }; proposal: VerkoopProposal | null }>(
			`/api/verkoopfacturen/${id}`,
		),
	exactCustomers: (entityCode: string) =>
		request<{ items: ExactCustomer[] }>(`/api/exact/customers/${encodeURIComponent(entityCode)}`),
	exactCreditors: (entityCode: string) =>
		request<{ items: ExactCreditor[] }>(`/api/exact/creditors/${encodeURIComponent(entityCode)}`),
	exactLedgers: (entityCode: string) =>
		request<{ items: ExactLedger[] }>(`/api/exact/ledgers/${encodeURIComponent(entityCode)}`),
	exactJournals: (entityCode: string) =>
		request<{ items: ExactJournal[] }>(`/api/exact/journals/${encodeURIComponent(entityCode)}`),
	exactEntityDefaults: (entityCode: string) =>
		request<{ defaults: ExactEntityDefaults | null }>(`/api/exact/entity-defaults/${encodeURIComponent(entityCode)}`),
	exactVatCodes: (entityCode: string) =>
		request<{ items: ExactVatCode[] }>(`/api/exact/vat-codes/${encodeURIComponent(entityCode)}`),
	exactCreditorDefaults: (entityCode: string, exactId: string) =>
		request<{ defaults: ExactCreditorDefaults | null }>(
			`/api/exact/creditors/${encodeURIComponent(entityCode)}/${encodeURIComponent(exactId)}/defaults`,
		),
	m365Apps: () => request<{ items: M365App[] }>('/api/m365/apps'),
	m365Mailboxes: () => request<{ items: M365Mailbox[] }>('/api/m365/mailboxes'),
	audit: (params: Record<string, string | number | undefined> = {}) => {
		const q = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== '') q.set(k, String(v));
		}
		const qs = q.toString();
		return request<{ count: number; items: AuditEvent[]; limit: number; offset: number }>(
			`/api/audit${qs ? `?${qs}` : ''}`,
		);
	},
};

export function documentPdfUrl(id: string): string {
	return `${API_BASE}/api/documents/${id}/pdf`;
}
